import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Pagination from './pagination/pagination.js';
import CreateTrackingData from './CreateTrackingData.js';
import TableEntry from './DataComponents/TableEntry.js';
import { supabase } from '../../../../components/supabase/config.js';
import 'bootstrap/dist/css/bootstrap.min.css';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Normalizes many possible inputs into:
 *   "MM-DD-YYYY HH:MM"
 *
 * Rules:
 * - If only date exists (MM-DD-YYYY or MM/DD/YYYY or YYYY-MM-DD), time defaults to "00:00" (midnight / 12:00 AM).
 * - If time exists, keep it.
 */
function normalizeToMMDDYYYY_HHMM(input) {
    if (!input) return '';

    const s = String(input).trim();
    if (!s) return '';

    // ISO datetime-local or timestamptz-ish: "YYYY-MM-DDTHH:MM..."
    const isoMatch = s.match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
    );
    if (isoMatch) {
        const [, yyyy, mm, dd, HH, MM] = isoMatch;
        return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
    }

    // ISO date only: "YYYY-MM-DD"
    const isoDateOnlyMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnlyMatch) {
        const [, yyyy, mm, dd] = isoDateOnlyMatch;
        return `${mm}-${dd}-${yyyy} 00:00`;
    }

    // "MM-DD-YYYY" optionally with time " HH:MM"
    const mdYDash = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (mdYDash) {
        const [, mm, dd, yyyy, HH, MM] = mdYDash;
        return `${mm}-${dd}-${yyyy} ${HH && MM ? `${HH}:${MM}` : '00:00'}`;
    }

    // "MM/DD/YYYY" optionally with time " HH:MM"
    const mdYSlash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (mdYSlash) {
        const [, mm, dd, yyyy, HH, MM] = mdYSlash;
        return `${mm}-${dd}-${yyyy} ${HH && MM ? `${HH}:${MM}` : '00:00'}`;
    }

    // Fallback: try Date parsing (may work for some formats)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        const mm = pad2(d.getMonth() + 1);
        const dd = pad2(d.getDate());
        const yyyy = d.getFullYear();
        const HH = pad2(d.getHours());
        const MM = pad2(d.getMinutes());
        return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
    }

    // If unparseable, return original
    return s;
}

/**
 * Converts normalized display "MM-DD-YYYY HH:MM"
 * into input value for <input type="datetime-local"> => "YYYY-MM-DDTHH:MM"
 */
function toDatetimeLocalValue(displayValue) {
    const normalized = normalizeToMMDDYYYY_HHMM(displayValue);
    if (!normalized) return '';

    const match = normalized.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})$/);
    if (!match) return '';

    const [, mm, dd, yyyy, HH, MM] = match;
    return `${yyyy}-${mm}-${dd}T${HH}:${MM}`;
}

/**
 * Converts <input type="datetime-local"> value "YYYY-MM-DDTHH:MM"
 * into display/storage "MM-DD-YYYY HH:MM"
 */
function fromDatetimeLocalValue(rawValue) {
    if (!rawValue) return '';
    const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) return '';
    const [, yyyy, mm, dd, HH, MM] = match;
    return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
}

function DataViewer() {
    const [filter, setFilter] = useState('');
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [editingEntry, setEditingEntry] = useState(null);
    const [creating, setCreating] = useState(false);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const { data: fetchedData, error } = await supabase.from('trackingfbt').select('*');
            if (error) {
                console.error('Error fetching data:', error);
            } else {
                setData(fetchedData);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    const filteredData = data.filter((item) => String(item.id || '').includes(filter));

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    // This now returns "MM-DD-YYYY HH:MM" and defaults to "00:00" if date-only
    const formatDate = (dateString) => normalizeToMMDDYYYY_HHMM(dateString);

    const showEditForm = (entry) => {
        setEditingEntry(entry);
        setShowModal(true);
    };

    const handleEdit = async () => {
        const { data: updatedData, error } = await supabase
            .from('trackingfbt')
            .update(editingEntry)
            .eq('id', editingEntry.id)
            .single();

        if (error) {
            console.error('Error updating entry:', error);
        } else {
            setData(data.map((entry) => (entry.id === updatedData.id ? updatedData : entry)));
            setShowModal(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // If user edits date_ordered in datetime-local, store as "MM-DD-YYYY HH:MM"
        if (name === 'date_ordered') {
            const formatted = fromDatetimeLocalValue(value);
            setEditingEntry((prev) => ({ ...prev, [name]: formatted }));
            return;
        }

        setEditingEntry((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <div className="container-xl">
            <div className="table-responsive">
                <div className="table-wrapper">
                    <div className="table-title">
                        <div className="row">
                            <div className="col-sm-5">
                                <h2>
                                    Data <b>Management</b>
                                </h2>
                            </div>
                            <div className="col-sm-7 text-right">
                                <button className="btn btn-danger" onClick={() => setCreating(true)}>
                                    <i className="material-icons">&#xE147;</i> <span>Add New Entry</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {creating ? (
                        <CreateTrackingData onSubmit={handleEdit} onCancel={() => setCreating(false)} />
                    ) : (
                        <>
                            <div className="row mb-4 justify-content-center">
                                <div className="col-sm-6">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by ID"
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                    />
                                </div>
                            </div>

                            <TableEntry data={currentItems} formatDate={formatDate} onEdit={showEditForm} />

                            <div className="clearfix">
                                <div className="hint-text">
                                    Showing <b>{currentItems.length}</b> out of <b>{filteredData.length}</b> entries
                                </div>
                                <Pagination
                                    itemsPerPage={itemsPerPage}
                                    totalItems={filteredData.length}
                                    paginate={setCurrentPage}
                                    currentPage={currentPage}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {editingEntry && (
                <Modal show={showModal} onHide={() => setShowModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Entry</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Sender</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="sender"
                                    value={editingEntry.sender || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Receiver</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="receiver"
                                    value={editingEntry.receiver || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Items</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="items"
                                    value={editingEntry.items || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Date Ordered (MM-DD-YYYY HH:MM)</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    name="date_ordered"
                                    value={toDatetimeLocalValue(editingEntry.date_ordered)}
                                    onChange={handleChange}
                                />
                                <Form.Text className="text-muted">
                                    If your existing data is date-only (MM-DD-YYYY), it will default to 00:00 (midnight / 12:00 AM).
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>No. of Boxes</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="no_of_box"
                                    value={editingEntry.no_of_box || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleEdit}>
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}

export default DataViewer;
